import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Eye,
  EyeOff,
  Mail,
  Lock
} from 'lucide-react';

// ============================================
// ONETRACK LOGIN
// Simple fullscreen login - consents asked after login
// ============================================

export default function Login({ onSwitchToRegister }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, sessionExpiredMessage, clearSessionExpiredMessage } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (sessionExpiredMessage) {
      clearSessionExpiredMessage();
    }
    setLoading(true);
    const result = await login(email, password);
    if (!result.success) setError(result.message);
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      padding: '1.5rem',
      paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
      paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))'
    }}>
      {/* Container for centering */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        animation: 'fadeIn 0.5s ease-out'
      }}>
        {/* Logo */}
        <div style={{
          width: '80px',
          height: '80px',
          background: '#FFEB3B',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          marginBottom: '2rem',
          boxShadow: '0 8px 32px rgba(255, 235, 59, 0.3)'
        }}>
          <img
            src="/taskmanager/onetrack192.png"
            alt="OneTrack"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 'clamp(26px, 6vw, 32px)',
          fontWeight: '700',
          color: 'white',
          textAlign: 'center',
          marginBottom: '0.5rem'
        }}>
          Kirjaudu sisään
        </h1>

        <p style={{
          color: '#94a3b8',
          fontSize: '15px',
          textAlign: 'center',
          marginBottom: '2rem'
        }}>
          Kirjaudu jatkaaksesi tehtävien parissa
        </p>

        {/* Session expired message */}
        {sessionExpiredMessage && (
          <div style={{
            width: '100%',
            padding: '1rem',
            background: 'rgba(251, 191, 36, 0.1)',
            border: '2px solid #FFEB3B',
            borderRadius: '12px',
            color: '#FFEB3B',
            fontSize: '14px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>⏱️</span>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Istunto vanhentunut</div>
              <div style={{ opacity: 0.9 }}>{sessionExpiredMessage}</div>
            </div>
          </div>
        )}

        {/* Login form */}
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          {/* Email field */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e2e8f0'
            }}>
              Sähköposti
            </label>
            <div style={{ position: 'relative' }}>
              <Mail
                size={20}
                color="#64748b"
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
              <input
                type="email"
                placeholder="matti@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem 0.875rem 3rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '2px solid #334155',
                  borderRadius: '12px',
                  fontSize: '15px',
                  outline: 'none',
                  color: '#ffffff',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFEB3B'}
                onBlur={(e) => e.target.style.borderColor = '#334155'}
                required
              />
            </div>
          </div>

          {/* Password field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e2e8f0'
            }}>
              Salasana
            </label>
            <div style={{ position: 'relative' }}>
              <Lock
                size={20}
                color="#64748b"
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Syötä salasanasi"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.875rem 3rem 0.875rem 3rem',
                  background: 'rgba(15, 23, 42, 0.8)',
                  border: '2px solid #334155',
                  borderRadius: '12px',
                  fontSize: '15px',
                  outline: 'none',
                  color: '#ffffff',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FFEB3B'}
                onBlur={(e) => e.target.style.borderColor = '#334155'}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  color: '#64748b'
                }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              padding: '0.875rem 1rem',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid #ef4444',
              borderRadius: '12px',
              color: '#fca5a5',
              fontSize: '14px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '1rem',
              background: loading ? '#475569' : '#FFEB3B',
              color: loading ? '#94a3b8' : '#0f172a',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {loading ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #94a3b8',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Kirjaudutaan...
              </>
            ) : (
              'Kirjaudu sisään'
            )}
          </button>

          {/* Forgot password */}
          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => {
                window.history.pushState({}, '', '/taskmanager/forgot-password');
                window.location.reload();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#FFEB3B',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
              onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
              onMouseOut={(e) => e.target.style.textDecoration = 'none'}
            >
              Unohditko salasanan?
            </button>
          </div>
        </form>

        {/* Divider */}
        <div style={{
          width: '100%',
          height: '1px',
          background: '#334155',
          margin: '2rem 0 1.5rem'
        }} />

        {/* Footer */}
        <p style={{
          color: '#64748b',
          fontSize: '12px',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          Kirjautumalla hyväksyt palvelun käyttöehdot ja tietosuojaselosteen.
        </p>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        input::placeholder {
          color: #64748b;
        }
      `}</style>
    </div>
  );
}
